import { Country } from '@utils/pageConfig/Types';
import { ClientSubscription } from '@models/Opa';
import { useCallback, useEffect, useState } from 'react';
import { useLogging } from '@ecosystem/newron-client-lib';
import saveErrorLog, { ErrorTagEnum } from '@utils/LogUtil';
import { getSubscriptionWithProductType } from '@utils/SubsciptionsUtils';
import { ProductType } from '@pages/product-analyzer/Product';
import { getRetailSubscription } from '@services/opa/Opa.service';

type UseFetchEntitledMarketsReturnType = {
  isLoading: boolean;
  entitledMarkets: Country[];
};
export const useFetchEntitledMarkets = (): UseFetchEntitledMarketsReturnType => {
  const [entitledMarkets, setEntitledMarkets] = useState<Country[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { error } = useLogging();

  const fetchMarkets = useCallback(async () => {
    try {
      //Call Persona API to get subscription
      const subscriptions = await getRetailSubscription();
      setIsLoading(true);
      const performanceAnalyzerSubscription = getSubscriptionWithProductType(
        subscriptions,
        ProductType.PerformanceAnalyzer,
      );
      const productAnalyzerSubscription = getSubscriptionWithProductType(subscriptions, ProductType.ProductAnalyzer);
      const marketPremiumSubscription = getSubscriptionWithProductType(subscriptions, ProductType.MarketPremium);
      const sellerInsightsSubscription = getSubscriptionWithProductType(subscriptions, ProductType.SellerInsights);
      const retailKadSubscription = getSubscriptionWithProductType(subscriptions, ProductType.RetailKad);

      const combinedSubscriptions = (performanceAnalyzerSubscription.result.restrictions ?? [])
        .concat(productAnalyzerSubscription.result.restrictions ?? [])
        .concat(marketPremiumSubscription.result.restrictions ?? [])
        .concat(sellerInsightsSubscription.result.restrictions ?? [])
        .concat(retailKadSubscription.result.restrictions ?? []);

      const markets = getMarkets(combinedSubscriptions);

      setEntitledMarkets(markets);
    } catch (err: any) {
      saveErrorLog(err, `UseFetchEntitledMarkets - Get subscription error.`, {
        category: ErrorTagEnum.ApiError,
        requestPayload: '',
      });
      error(err);
    } finally {
      setIsLoading(false);
    }
  }, [error]);

  const getMarkets = (combinedSubscriptions: ClientSubscription[]): Country[] => {
    return combinedSubscriptions
      .filter(
        (sub: ClientSubscription, index: number, array: ClientSubscription[]) =>
          array.findIndex((otherSub) => sub.countryObject.countryCode === otherSub.countryObject.countryCode) === index,
      )
      .map((subscription: ClientSubscription) => ({
        id: subscription.countryObject.countryCode,
        name: subscription.countryObject.countryName,
        currency: subscription.countryObject.currency.currencyCode,
      }))
      .sort((s1, s2) => (s1.name < s2.name ? -1 : 1));
  };

  useEffect(() => {
    fetchMarkets();
  }, [fetchMarkets]);

  return { entitledMarkets, isLoading };
};
